import {compact} from 'lodash';
import React, {ReactNode, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {CardModel} from '../../../models/api/cardModel';
import {CardNoteModel} from '../../../models/api/cardNoteModel';
import {
  ContactDetailSectionsExpandState,
  ContactDetailsSectionsEnum,
} from '../../../models/api/localPreferencesModel';
import {PreviewCardMergeModel} from '../../../models/api/previewCardMergeModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {Accordion} from '../../common/accordion/accordion';
import {AccordionSectionData} from '../../common/accordion/accordionSection';
import {
  buildContactDetailsHoverPayload,
  ContactDetailsHoverSectionsEnum,
} from '../contactDetailsTrackingHelpers';
import {ContactHeader} from '../contactHeader/contactHeader';
import {ContactInformation} from '../contactInformation/contactInformation';
import {ContactInformationAddButton} from '../contactInformation/contactInformationAddButton';
import {ContactNoteAddNoteButton} from '../contactNotes/contactNoteAddButton';
import {ContactNotes, ContactNotesHandle} from '../contactNotes/contactNotes';
import {ContactNotesContainer} from '../contactNotes/contactNotesContainer';
import {ContactDetailsConversationListContainer} from '../conversationList/contactDetailsConversationListContainer';
import {ContactDetailsHeaderLoadingSkeleton} from '../loadingSkeleton/contactDetailsHeaderLoadingSkeleton';
import {ContactDetailsSectionLoadingSkeleton} from '../loadingSkeleton/contactDetailsSectionLoadingSkeleton';

/*
 * Constants.
 */

const sectionAccordionPadding = 16;
const titleAccordionPadding = '13px 9px 10px 9px';

/*
 * Interface.
 */

export type OnContactDetailsSectionToggledHandler = (
  section: ContactDetailsSectionsEnum,
  isExpanded: boolean,
) => void;

interface BuildContactDetailsAccordionSectionBaseOptions {
  expandState: ContactDetailSectionsExpandState;
  contact?: VisualContactModel;
  card?: CardModel;
  onContactDetailsSectionToggled: OnContactDetailsSectionToggledHandler;
}

interface BuildNotesSectionOptions extends BuildContactDetailsAccordionSectionBaseOptions {
  panelId: ContactDetailsPanelId;
  notesRef?: RefObject<ContactNotesHandle>;
  previewNotes?: ReadonlyArray<CardNoteModel>;
}

interface BuildConversationListSectionOptions extends BuildContactDetailsAccordionSectionBaseOptions {
  panelId: ContactDetailsPanelId;
  shouldHideConversationSection?: boolean;
}

interface BuildAccordionSectionsOption
  extends BuildContactDetailsAccordionSectionBaseOptions,
    BuildNotesSectionOptions,
    BuildConversationListSectionOptions {}

interface BuildContactDetailsMergePreviewProps {
  panelId: ContactDetailsPanelId;
  previewCardMerge: PreviewCardMergeModel;
  contact?: VisualContactModel;
  noteIds?: ReadonlyArray<number>;
}

/*
 * Styles.
 */

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
`;

const StyledAccordion = styled(Accordion)`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Helpers.
 */

export function buildAccordionSections({
  expandState,
  card,
  contact,
  panelId,
  previewNotes,
  shouldHideConversationSection,
  notesRef,
  onContactDetailsSectionToggled,
}: BuildAccordionSectionsOption) {
  return compact([
    buildContactInformationSection({expandState, card, contact, onContactDetailsSectionToggled}),
    buildConversationListSection({
      panelId,
      expandState,
      contact,
      card,
      shouldHideConversationSection: shouldHideConversationSection ?? false,
      onContactDetailsSectionToggled,
    }),
    buildNotesSection({
      panelId,
      expandState,
      card,
      previewNotes,
      contact,
      notesRef,
      onContactDetailsSectionToggled,
    }),
  ]);
}

export function buildContactInformationSection(
  options: BuildContactDetailsAccordionSectionBaseOptions,
): AccordionSectionData {
  const {expandState, card, contact, onContactDetailsSectionToggled} = options;
  return {
    id: ContactDetailsSectionsEnum.CONTACT_INFORMATION,
    title: renderSectionTitle(<Trans id="contact-details-contact-information-title">About</Trans>),
    titleAction: expandState[ContactDetailsSectionsEnum.CONTACT_INFORMATION] ? (
      <ContactInformationAddButton contact={contact} />
    ) : undefined,
    isExpandedByDefault: expandState[ContactDetailsSectionsEnum.CONTACT_INFORMATION],
    onSectionToggled: (isExpanded: boolean) => {
      onContactDetailsSectionToggled(ContactDetailsSectionsEnum.CONTACT_INFORMATION, isExpanded);
    },
    sectionPadding: sectionAccordionPadding,
    titlePadding: titleAccordionPadding,
    content: <ContactInformation card={card} contact={contact} />,
  };
}

export function buildNotesSection(options: BuildNotesSectionOptions): AccordionSectionData | undefined {
  const {expandState, panelId, card, previewNotes, contact, notesRef, onContactDetailsSectionToggled} =
    options;
  const shouldRenderNotesDirectly = Boolean(previewNotes && card);

  const renderContent = () => {
    if (shouldRenderNotesDirectly && previewNotes) {
      return (
        <ContactNotes
          noteLoadingStatus={AsyncStatusesEnum.LOADED}
          card={card}
          notes={previewNotes}
          userNamespace=""
          createCardNoteAsync={async () => {}}
          createNewCardAsync={async () => undefined}
        />
      );
    }
    return <ContactNotesContainer panelId={panelId} card={card} contact={contact} notesRef={notesRef} />;
  };

  return {
    id: ContactDetailsSectionsEnum.NOTES,
    title: renderSectionTitle(<Trans id="contact-details-contact-notes-title">Notes</Trans>),
    titleAction: expandState[ContactDetailsSectionsEnum.NOTES] ? (
      <ContactNoteAddNoteButton notesRef={notesRef} />
    ) : undefined,
    isExpandedByDefault: expandState[ContactDetailsSectionsEnum.NOTES],
    onSectionToggled: (isExpanded: boolean) => {
      onContactDetailsSectionToggled(ContactDetailsSectionsEnum.NOTES, isExpanded);
    },
    sectionPadding: sectionAccordionPadding,
    titlePadding: titleAccordionPadding,
    content: renderContent(),
    shouldScrollOnExpand: true,
  };
}

export function buildConversationListSection(options: BuildConversationListSectionOptions) {
  const {panelId, expandState, contact, card, shouldHideConversationSection, onContactDetailsSectionToggled} =
    options;

  // If we do not have any contacts, we can not search for conversations.
  if (shouldHideConversationSection || !contact) {
    return null;
  }

  return {
    id: ContactDetailsSectionsEnum.CONVERSATIONS,
    title: renderSectionTitle(<Trans id="contact-details-conversations-title">Conversations</Trans>),
    isExpandedByDefault: expandState[ContactDetailsSectionsEnum.CONVERSATIONS],
    onSectionToggled: (isExpanded: boolean) => {
      onContactDetailsSectionToggled(ContactDetailsSectionsEnum.CONVERSATIONS, isExpanded);
    },
    sectionPadding: sectionAccordionPadding,
    titlePadding: titleAccordionPadding,
    content: (
      <ContactDetailsConversationListContainer
        panelId={panelId}
        card={card}
        visualContact={contact}
        onConversationHoverTrack={() => {
          if (!card) {
            return;
          }
          trackInterfaceEvent(
            InterfaceEventsEnum.P4_CONTACT_DETAILS_HOVER,
            buildContactDetailsHoverPayload(card, ContactDetailsHoverSectionsEnum.CONVERSATION),
          );
        }}
      />
    ),
  };
}

const renderSectionTitle = (text: ReactNode) => <StyledTitleDiv>{text}</StyledTitleDiv>;

export function renderContactDetailsMergePreview(options: BuildContactDetailsMergePreviewProps) {
  const {panelId, previewCardMerge, contact, noteIds} = options;

  const previewExpandedState = {
    [ContactDetailsSectionsEnum.CONTACT_INFORMATION]: true,
    // Only default notes open only if the notes total don't match.
    [ContactDetailsSectionsEnum.NOTES]: previewCardMerge.notes.length !== (noteIds ?? []).length,
    [ContactDetailsSectionsEnum.SUMMARY]: false,
    [ContactDetailsSectionsEnum.CONVERSATIONS]: false,
  };

  return (
    <>
      <ContactHeader contact={contact} card={previewCardMerge.card} />
      <StyledAccordion
        key="merge-accordion"
        allowMultiSelect
        sections={buildAccordionSections({
          expandState: previewExpandedState,
          card: previewCardMerge.card,
          contact,
          panelId,
          previewNotes: previewCardMerge.notes,
          // Disable saving contact details section toggle in preview mode.
          onContactDetailsSectionToggled: () => {},
          shouldHideConversationSection: true,
        })}
      />
    </>
  );
}

export function renderContactDetailsLoadingSections(
  isMerging: boolean,
  sectionExpandState: ContactDetailSectionsExpandState,
) {
  return (
    <>
      <ContactDetailsHeaderLoadingSkeleton />
      <ContactDetailsSectionLoadingSkeleton
        section={ContactDetailsSectionsEnum.CONTACT_INFORMATION}
        shouldHideContentSkeleton={!sectionExpandState[ContactDetailsSectionsEnum.CONTACT_INFORMATION]}
      />
      {!isMerging && (
        <ContactDetailsSectionLoadingSkeleton
          section={ContactDetailsSectionsEnum.CONVERSATIONS}
          shouldHideContentSkeleton={!sectionExpandState[ContactDetailsSectionsEnum.CONVERSATIONS]}
        />
      )}
      <ContactDetailsSectionLoadingSkeleton
        section={ContactDetailsSectionsEnum.NOTES}
        shouldHideContentSkeleton={!sectionExpandState[ContactDetailsSectionsEnum.NOTES]}
        shouldRenderBottomBorder
      />
    </>
  );
}
